import React, { useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useEnrollmentFormData } from './StudentEnrollmentFormContext';

const Container = styled.div`
  max-width: 906px;
  margin: 10px auto;
  padding: 10px 32px;
  font-family: 'serif';
  font-weight: bold;
`;

const Logo = styled.img`
  display: block;
  margin: 0 0 16px 0;
  max-width: 360px;
`;

const DownloadButton = styled.button`
  background: #334293;
  color: white;
  padding: 12px 32px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 32px auto 0 auto;
  display: block;
  &:hover {
    background: #2c3e50;
    transform: translateY(-2px);
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 0;
`;

const Subtitle = styled.div`
  text-align: center;
  font-size: 0.9rem;
  font-weight: 800;
  margin-bottom: 10px;
`;

const Section = styled.div`
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  font-size: 1rem;
`;

const Td = styled.td`
  border: 1px solid #222;
  padding: 6px 8px;
`;

const Th = styled.th`
  border: 1px solid #222;
  padding: 6px 8px;
  background: #f5f5f5;
`;

const Blank = styled.span`
  display: inline-block;
  border-bottom: 1px solid #333;
  margin: 0 4px;
  padding: 0 4px;
  font-weight: 500;
`;

const Checkbox = ({ checked }) => (
  <span className="checkbox" style={{ fontWeight: 700, fontSize: '1.1rem', marginRight: 6 }}>{checked ? '✓' : '□'}</span>
);

const PrintHideStyle = createGlobalStyle`
  @media print {
    .print-hide {
      display: none !important;
    }
    
    @page {
      margin: 0.5in;
      size: A4;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Reduce font sizes for print */
    h2 {
      font-size: 1.2rem !important;
      margin-bottom: 0.5rem !important;
    }
    
    div {
      font-size: 0.7rem !important;
      line-height: 1.1 !important;
    }
    
    span {
      font-size: 0.7rem !important;
    }
    
    /* Reduce margins and padding for print */
    .container {
      margin: 0 !important;
      padding: 0.2in !important;
      max-width: none !important;
    }
    
    /* Reduce spacing between sections */
    hr {
      margin: 0.1rem 0 !important;
    }
    
    /* Reduce margins on sections */
    .section {
      margin-bottom: 0.2rem !important;
      margin-top: 0.2rem !important;
    }
    
    /* Reduce line height for better fit */
    .section-text {
      line-height: 1.1 !important;
      margin-bottom: 0.2rem !important;
    }
    
    /* Reduce spacing in form fields */
    .form-field {
      margin-bottom: 0.2rem !important;
    }
    
    /* Reduce checkbox size */
    .checkbox {
      font-size: 0.8rem !important;
    }
    
    /* Reduce signature area height */
    .signature-area {
      height: 15px !important;
    }
    
    /* Reduce race codes section spacing */
    .race-codes {
      margin-left: 0.3in !important;
    }
    
    .race-code-item {
      margin-bottom: 0.1rem !important;
    }
  }
`;

const BackgroundCheckForm = () => {
  const docRef = useRef();
  const { data: formData, loading, error } = useEnrollmentFormData();

  const handleDownload = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading student information...
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
          Error loading student information: {error}
        </div>
      </Container>
    );
  }

  if (!formData) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          No student data found.
        </div>
      </Container>
    );
  }

  // Extract student data from formData
  const student = formData;

  return (
    <Container className="container">
      <PrintHideStyle />
      <div ref={docRef}>
        <Logo src={process.env.PUBLIC_URL + '/state_of_illionis_logo.png'} alt="State of Illinois Logo" />
        <Title>Health Care Worker Background Check</Title>

        <Subtitle>Authorization and Disclosure for Criminal History Records Information (CHRI) Check</Subtitle>
        <hr style={{ margin: 0, borderWidth: 'initial', borderColor: 'black' }} />

        <Section className="section section-text" style={{ fontSize: '0.98rem', marginBottom: 5, lineHeight: 1, fontWeight: 'bold' }}>
          I hereby authorize the Illinois Department of Public Health (the Department), the Department's designee, educational entities that train and/or test health care workers, staffing agencies, my current or potential employer, or a health care facility where I want to volunteer to initiate/request a CHRI check on me. I further authorize the Illinois State Police (ISP) and/or the Federal Bureau of Investigation (FBI) to release information and photographs relative to the existence or nonexistence of any criminal record, which it might have concerning me, to any initiator/requester solely to determine my suitability for training or testing in a health care training program, employment, continued employment, or to work as a volunteer. I further authorize any entity that maintains criminal records and photographs relating to me, including but not limited to a local unit of government in any state, to release those records and photographs to ISP, FBI, or the Department. I authorize the Department to provide any health care facility, training program or staffing agency, to which I have provided this authorization and disclosure form, a copy of my ISP CHRI and a determination of eligibility of the FBI CHRI. I certify that the ISP, FBI, any entity that maintains criminal records and photographs, the Department, and any of their employees or officers who furnish this information shall be held harmless from all liability, which may be incurred as a result of releasing such information. I further acknowledge that a educational entity or a health care employer shall not be liable for the failure to hire or retain me as an applicant, student, employee, or volunteer if I have been convicted of committing or attempting to commit one or more of the offenses stated in the Health Care Worker Background Check Act (225 ILCS 46/25).
        </Section>
        <hr style={{ margin: 0, borderWidth: 'initial', borderColor: 'black' }} />

        <Section className="section section-text" style={{ fontSize: '0.98rem', marginBottom: 5, lineHeight: 1, fontWeight: 'bold' }}>
          I understand that any false statements or deliberate omissions on this document may be grounds for disqualification from employment, training, or volunteering, if discovered after employment, training, or volunteering begins, and can result in discipline up to and including my termination of employment, being a volunteer, or a student.
        </Section>
        <hr style={{ margin: 0, borderWidth: 'initial', borderColor: 'black' }} />
        <Section className="section section-text" style={{ fontSize: '0.98rem', marginBottom: 5, lineHeight: 1, fontWeight: 'bold' }}>
          I understand that the information requested below regarding gender, race, height, eye color, hair color, weight, place of birth and date of birth is for the sole purpose of identification and the accurate gathering of the criminal history record information, and that it will not be used to discriminate against me in violation of the law. I understand that the provision of my Social Security number is required by law. A facsimile or photographic copy of this authorization will be as valid as the original.
        </Section>
        <hr style={{ margin: 0, borderWidth: 'initial', borderColor: 'black' }} />

        <Section className="section" style={{ fontSize: '1rem', lineHeight: 2, marginBottom: 16, marginTop: 16, fontWeight: 'bold' }}>
          <div className="form-field" style={{ marginBottom: 22 }}>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>First Name</span>
            <Blank style={{minWidth: '18%'}}>{student.firstName || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 16, fontWeight: 'bold' }}>Full Middle Name</span>
            <Blank style={{minWidth: '18%'}}>{student.bc_full_middle_name || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 16, fontWeight: 'bold' }}>Last Name</span>
            <Blank style={{minWidth: '18%'}}>{student.lastName || ''}</Blank>
          </div>

          <div className="form-field">
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>Mailing Address</span>
            <Blank style={{minWidth: '120px'}}>{student.enroll_address || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 16, fontWeight: 'bold' }}>City:</span>
            <Blank style={{minWidth: '80px'}}>{student.enroll_city || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 8, fontWeight: 'bold' }}>State:</span>
            <Blank style={{minWidth: '50px'}}>{student.enroll_state || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 8, fontWeight: 'bold' }}>Zip Code</span>
            <Blank style={{minWidth: '80px'}}>{student.enroll_zip || ''}</Blank>
          </div>

          <div className="form-field" style={{ marginBottom:22,marginTop: 22 }}>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>Other Names Used</span>
            <Blank style={{ minWidth: '200px' }}>{student.bc_other_names || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 16, fontWeight: 'bold' }}>Telephone</span>
            <Blank style={{ minWidth: '200px' }}>{student.enroll_phone_c || ''}</Blank>
          </div>
          <div className="form-field" style={{ marginBottom: 22 }}>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>States Where You Have Lived?</span>
            <Blank style={{minWidth: '450px'}}>{student.bc_states_lived || ''}</Blank>
          </div>

          <div className="form-field" style={{ marginBottom: 8 }}>
            <span style={{ marginRight: 8,fontWeight: 'normal' }}>{<Checkbox checked={student.med_gender === 'Male'}  />} Male</span>
            <span style={{ marginRight: 8,fontWeight: 'normal' }}>{<Checkbox checked={student.med_gender === 'Female'}  />}Female</span>
            <span style={{ marginLeft: 30, marginRight: 8, fontWeight: 'bold' }}>Race</span>
            <Blank style={{minWidth: '40px'}}>{student.bc_race || ''}</Blank>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>Height</span>
            <Blank style={{minWidth: '20px'}}>{student.bc_height_ft || ''}</Blank>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>Weight</span>
            <Blank style={{minWidth: '20px'}}>{student.bc_weight || ''}</Blank>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>Date of Birth</span>
            <Blank style={{minWidth: '20px'}}>{student.bc_dob || ''}</Blank>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>Social Security Number</span>
            <Blank style={{minWidth: '20px'}}>{student.bc_ssn || ''}</Blank>
          </div>
          <div>
            <span style={{ fontSize: '0.8rem', marginLeft: 166, fontWeight: 'bold' }}>(Enter a letter from below)</span>

          </div>
          <div className="form-field" style={{ marginBottom: 8,marginLeft: 166 }}>
            <span style={{ marginRight: 8,  fontWeight: 'bold' }}>Hair Color</span>
            <Blank>{student.bc_hair_color || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 16, fontWeight: 'bold' }}>Eye Color</span>
            <Blank>{student.bc_eye_color || ''}</Blank>
            <span style={{ marginRight: 8, marginLeft: 16, fontWeight: 'bold' }}>Place of Birth</span>
            <Blank>{student.bc_place_of_birth || ''}</Blank>
          </div>
        </Section>
        <b style={{ marginRight: 12,marginLeft: 16 }}>Race </b> 

        
          <div  className="race-codes" style={{ display: 'flex', marginLeft: 63 }}> 
            <div className="race-code-item" style={{ display: 'flex' }}>
              A
            </div>
            <div style={{ marginLeft: 26 }}>Chinese, Japanese, Filipino, Korean, Polynesian, Indian, Indonesian, Asian Indian, Samoan, or any other Pacific Islander.</div>
          </div>
      
        <div className="race-codes" style={{marginLeft: 61}}>
          <div className="race-code-item" style={{ display: 'flex' }}>
            <div>
              B
            </div>
            <div style={{ marginLeft: 26 }}>Black or African American(Not Hispanic or Latino)</div>
          </div>
        </div>
        <div className="race-codes" style={{marginLeft: 61}}>
          <div className="race-code-item" style={{ display: 'flex' }}>
            <div>
              H
            </div>
            <div style={{ marginLeft: 26 }}>
            Hispanic or Latino (Mexican, Puerto Rican, Cuban, Central or South American, or other Spanish culture or origin)
            </div>
          </div>
        </div>

        <div className="race-codes" style={{marginLeft: 61}}>
          <div className="race-code-item" style={{ display: 'flex' }}>
            <div>
              I
            </div>
            <div style={{ marginLeft: 30 }}>American Indian, Eskimo, or Alaskan native, or a person having origins in any of the 48 contiguous states of the United States or Alaska who maintains
              cultural identification through tribal affiliation or community recognition.
            </div>
          </div>
        </div>


        <div className="race-codes" style={{marginLeft: 61}}>
          <div className="race-code-item" style={{ display: 'flex' }}>

            <div>
              U
            </div>
            <div style={{ marginLeft: 24 }}>Of undeterminable race. Of Untold mixture.</div>
          </div>
        </div>

        <div className="race-codes" style={{marginLeft: 61}}>
          <div className="race-code-item" style={{ display: 'flex' }}>
            <div>
              W
              </div>
            <div style={{ marginLeft: 20 }}>Caucasian (not Hispanic or Latino)</div>
          </div>
        </div>
        <Section className="section" style={{ fontSize: '0.98rem', marginBottom: 8, marginTop: 16, fontWeight: 'bold' }}>
          <b>Have you ever had an administrative finding of Abuse, Neglect or Theft?</b> {<Checkbox checked={student.bc_abuse === 'Yes'} />}Yes {<Checkbox checked={student.bc_abuse === 'No'} />}No
          If "Yes," give full details and state.
          Continue on back if more space is
          needed.
          {student.bc_abuse === 'Yes' && student.bc_abuse_details && (
            <div style={{ marginTop: '10px', padding: '10px', fontStyle: 'italic' }}>
            {student.bc_abuse_details}
            </div>
          )}
        </Section>
        <Section className="section" style={{ fontSize: '0.98rem', margin:"30px 0", fontWeight: 'bold' }}>
          <b>Have you ever been convicted of a criminal offense other than a minor traffic violation?</b> {<Checkbox checked={student.bc_convicted === 'Yes'} />}Yes {<Checkbox checked={student.bc_convicted === 'No'} />}No
          If "Yes," give full details and state. Continue on back if more space is
          needed.
          {student.bc_convicted === 'Yes' && student.bc_convicted_details && (
            <div style={{ marginTop: '10px', padding: '10px', fontStyle: 'italic' }}>
             {student.bc_convicted_details}
            </div>
          )}
        </Section>
        <Section className="section section-text" style={{ fontSize: '0.98rem', marginBottom: 8, fontWeight: 'bold', marginTop: 70 }}>
          I certify that the above is true and correct and give my consent for my name to appear on Department's Health Care Worker Registry with the results of my criminal history records check.
        </Section>
        <Section className="section" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ flex: 1, marginRight: 32 }}>
                  {student.student_ack_signature_data ? (
                  <img src={student.student_ack_signature_data} alt="Student Signature" style={{ maxHeight: '40px', borderBottom: '1px solid #000', width: '100%', objectFit: 'contain' }} />
                  ) : (
                  <Blank className="signature-area" style={{ minWidth: '100%', height: '20px' }}></Blank>
                )}
                <div style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: 4, fontWeight: 'bold' }}>(Signature)</div>
              </div>
              <div style={{ flex: 1, alignContent: 'end' }}>
                <Blank className="signature-area" style={{ minWidth: "100%", height: '20px',textAlign: 'center' }}>{student.bc_parent_signature_date || ''}</Blank>
                <div style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: 4, fontWeight: 'bold' }}>(Date)</div>
              </div>
            </div>
          </div>

          <Section className="section section-text" style={{ fontSize: '0.98rem', marginBottom: 16, lineHeight: 1.5, fontWeight: 'bold' }}>
            As the parent or guardian of the above named individual, who is younger than the age of 17, I give my consent for this named individual to have a criminal history records check.
          </Section>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ flex: 1, marginRight: 32,alignContent: 'end' }}>
                {student.bc_parent_signature_data ? (
                  <img src={student.bc_parent_signature_data} alt="Parent/Guardian Signature" style={{ maxHeight: '40px', width: '100%', objectFit: 'contain', borderBottom: '1px solid #000' }} />
                  ) : (
                  <Blank className="signature-area" style={{ minWidth: '100%', height: '20px' }}></Blank>
                )}
                <div style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: 4, fontWeight: 'bold' }}>(Signature of Parent or Guardian when applicable)</div>
              </div>
              <div style={{ flex: 1, alignContent: 'end' }}>
                <Blank className="signature-area" style={{ minWidth: '100%', height: '20px',textAlign: 'center' }}>{student.bc_parent_signature_date || ''}</Blank>
                <div style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: 4, fontWeight: 'bold' }}>(Date)</div>
              </div>
            </div>
          </div>
        </Section>
        <Section className="section" style={{ fontSize: '0.98rem', marginBottom: 8, textAlign: 'center', fontWeight: 700 }}>
          Health Care Worker Registry, 525 W. Jefferson St., Springfield, IL 62761 Phone: 217-785-5133
        </Section>
        <Section className="section" style={{ fontSize: '0.98rem', marginBottom: 8, textAlign: 'center', fontWeight: 700 }}>
          *** ALL FIELDS MUST BE COMPLETED OR APPLICATION WILL NOT BE PROCESSED ***
        </Section>
      </div>
      <DownloadButton className="print-hide" onClick={handleDownload}>Print Form</DownloadButton>
    </Container>
  );
};

export default BackgroundCheckForm; 